import { ChangeEvent, useCallback, useMemo } from 'react';
import locale from '@glu/locale';
import { Input, Select } from '@glu/form-components';
import classNames from 'classnames';
import { IFilterable, IFilterProps, INumberFilterValues } from '../Filters/Filters';
import useStyles from './NumberFilter.styles';

export type TNumberFilterParseFilterValue = ({
  event,
  filterable,
  input,
  value,
}: {
  filterable: INumberFilterFilterable;
  event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>;
  input: string;
  value?: INumberFilterValues;
}) => any;

interface INumberFilterOptions
  extends Pick<NonNullable<IFilterable['filterOptions']>, 'numberFilters'> {
  parseFilterValue?: TNumberFilterParseFilterValue;
}

export interface INumberFilterFilterable extends Omit<IFilterable, 'filter' | 'filterOptions'> {
  filter?: INumberFilterValues;
  filterOptions?: INumberFilterOptions;
}

export interface INumberFilterProps extends Omit<IFilterProps, 'filterable'> {
  filterable: INumberFilterFilterable;
  /** The current value of the filter. Since state is not managed
   * locally this is required to display any current filter values.
   */
  value?: INumberFilterValues;
}

export const defaultParseNumberFilterValue: TNumberFilterParseFilterValue = ({
  event,
  filterable,
  input,
  value = { filter: '', filterTo: '', type: 'equals' },
}) => ({
  filterable,
  value: { ...value, [input]: event.target.value },
});

const NumberFilter = ({ filterable, onChange, value }: INumberFilterProps) => {
  const classes = useStyles();
  const { field, filterOptions } = filterable;
  const { filter = '', filterTo = '', type = 'equals' } = value || {};
  const inRange = type === 'inRange';

  const numberFilterTypes = useMemo(
    () => [
      { label: locale.get('dataComponents.equals'), value: 'equals' },
      { label: locale.get('dataComponents.notEqual'), value: 'notEqual' },
      { label: locale.get('dataComponents.isLessThan'), value: 'lessThan' },
      { label: locale.get('dataComponents.lessThanOrEqual'), value: 'lessThanOrEqual' },
      { label: locale.get('dataComponents.isGreaterThan'), value: 'greaterThan' },
      { label: locale.get('dataComponents.greaterThanOrEqual'), value: 'greaterThanOrEqual' },
      { label: locale.get('dataComponents.between'), value: 'inRange' },
    ],
    []
  );

  const handleSelectChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const parseFilterParams = {
        value,
        filterable,
        event,
        input: 'type',
      };
      const changeValue = (filterOptions?.parseFilterValue || defaultParseNumberFilterValue)(
        parseFilterParams
      );
      onChange(changeValue);
    },
    [value, filterable, filterOptions, onChange]
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const parseFilterParams = {
        value,
        filterable,
        event,
        input: 'filter',
      };
      const changeValue = (filterOptions?.parseFilterValue || defaultParseNumberFilterValue)(
        parseFilterParams
      );
      onChange(changeValue);
    },
    [value, filterable, filterOptions, onChange]
  );

  const handleInputTwoChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const parseFilterParams = {
        value,
        filterable,
        event,
        input: 'filterTo',
      };
      const changeValue = (filterOptions?.parseFilterValue || defaultParseNumberFilterValue)(
        parseFilterParams
      );
      onChange(changeValue);
    },
    [filterOptions, filterable, onChange, value]
  );

  return (
    <div className={classes.root}>
      <Select
        className={classNames({
          [classes.minWidth]: !inRange,
          [classes.maxWidth]: inRange,
        })}
        disableInputAPI
        labelText={locale.get('dataComponents.numberFilterType')}
        name={field}
        onChange={handleSelectChange}
        options={filterOptions?.numberFilters || numberFilterTypes}
        screenReaderLabel
        value={type}
      />
      <Input
        className={classes.input}
        disableInputAPI
        labelText={
          type === 'inRange'
            ? locale.get('dataComponents.enterMin')
            : locale.get('dataComponents.numberInput')
        }
        name={`${field}-numberFilter`}
        onChange={handleInputChange}
        screenReaderLabel
        type="number"
        value={filter}
      />
      {type === 'inRange' && (
        <Input
          className={classes.input}
          disableInputAPI
          labelText={locale.get('dataComponents.enterMax')}
          name={`${field}-numberFilterTwo`}
          onChange={handleInputTwoChange}
          screenReaderLabel
          type="number"
          value={filterTo}
        />
      )}
    </div>
  );
};

export default NumberFilter;
